import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { DeliveryType } from 'webapp/enums/enums';
import { NotificationFilterType, NotificationScheduleType } from 'webapp/enums/notifications';

import type { BoardShare } from 'webapp/models-custom';
import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import NotificationBase from 'webapp/models-custom/notification/base';

export default class NotificationBoardShare extends NotificationBase {
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/boards')
	declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;

	scheduleType = NotificationScheduleType.UPDATE;
	targetObject: BoardShare;
	typeColor = 'var(--databox-brand-500)';
	typeIcon: IconName = 'Databoards';

	constructor(model: BoardShare) {
		super();
		this.targetObject = model;
	}

	get type() {
		return this.targetObject.isLoop
			? NotificationFilterType.LOOP
			: NotificationFilterType.BOARD;
	}

	get title() {
		const target = this.targetObject.target;
		return target instanceof Board ? target.name : target?.title || '';
	}

	get subtitleText() {
		const target = this.targetObject.target;
		const itemCount = !target || target instanceof Board ? 1 : target.boardCount;
		return `${itemCount} dashboard${itemCount === 1 ? '' : 's'}`;
	}

	get isMine() {
		return this.targetObject.isMine;
	}

	override get canPreview() {
		return true;
	}

	get schedule() {
		return this.targetObject.schedule;
	}

	get frequency() {
		const frequency = this.mmNotificationsService.getFrequencyFromSchedule(this.schedule.type);
		return frequency ? [frequency] : [];
	}

	override get frequencySortProp() {
		return this.schedule.deliveryTimeLevel;
	}

	get frequencyTooltipTextTop() {
		const schedule = this.schedule;
		return `${schedule.typeText} ${schedule.timeText}`;
	}

	get createdBy() {
		return this.targetObject.createdBy;
	}

	get createdOn() {
		return this.targetObject.created;
	}

	get deliveryTypes() {
		const deliveryTypes: DeliveryType[] = [];
		const boardShare = this.targetObject;
		if (boardShare.isEmailDelivery) {
			deliveryTypes.push(DeliveryType.EMAIL);
		}
		if (boardShare.isSlackDelivery) {
			deliveryTypes.push(DeliveryType.SLACK);
		}
		return deliveryTypes;
	}

	get emailRecipients() {
		// cast existing emails into user ids
		const users = this.mmUsersService.activatedUserRecords;
		return this.targetObject.recipients.map((email) => {
			const userId = users.find((u) => u.email === email)?.id;
			return userId ? +userId : email;
		});
	}

	get allRecipients() {
		return this.targetObject.mappedRecipients;
	}

	override preview() {
		const boardOrLoop = this.targetObject.target;
		if (boardOrLoop instanceof Board) {
			this.mmBoardsService.openPreview(boardOrLoop, 'notifications');
		} else if (boardOrLoop instanceof BoardLoop) {
			this.mmBoardLoopsService.openPreviewPopup(boardOrLoop, 'notifications');
		}
	}

	delete() {
		const boardShare = this.targetObject;
		if (this.isMine) {
			void boardShare.deleteAndCatch();
		} else {
			const email = this.currentUserService.model?.email || '';
			boardShare.recipients = boardShare.recipients.filter((e) => e !== email);
			void boardShare.saveAndCatch();
		}
	}
}
